import { styled } from "@mui/material/styles";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import {
  type GridColDef,
  GridToolbarContainer,
  type GridValueFormatterParams,
} from "@mui/x-data-grid-pro";
import { Stack } from "@mui/material";
import React from "react";
import { format } from "date-fns";
import { type AnalyzedCorporation } from "@/features/commercialPictureAnalysis/types";
import OutputMenu from "@/features/commercialPictureAnalysis/components/OutputMenu";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";

interface IProps {
  row: AnalyzedCorporation[] | undefined;
  requestId: number | undefined;
  disabledAction: boolean;
  onDownloadComplete?: () => void;
}

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    background: rgba(0, 0, 0, 0.04);
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

const makeGridColDef = (): GridColDef[] => {
  return [
    {
      field: "id",
      headerName: "No",
      width: 90,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "corporationType",
      headerName: "法人種別",
      width: 140,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "tradeName",
      headerName: "商号・名称",
      width: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "corporateIdentificationNumber",
      headerName: "会社法人等番号",
      width: 180,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "headOfficeAddress",
      headerName: "本店・主たる事務所",
      width: 300,
      sortable: false,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "createdAt",
      type: "dateTime",
      headerName: "解析日時",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
  ];
};

const CustomToolbar: React.FC<IProps> = (props) => {
  const isReadOnly = useReadOnlyUser();
  const disableOutput = props.disabledAction || props.requestId == null;
  const requestId = props.requestId ?? 0;

  return (
    <GridToolbarContainer
      sx={{
        // https://mui.com/material-ui/react-button/#sizes と同様のpaddingを設定
        padding: "6px 16px",
      }}
    >
      <Stack direction={"row"} spacing={1}>
        <OutputMenu
          disabled={disableOutput}
          requestId={requestId}
          isReadOnly={isReadOnly}
          onDownloadComplete={props.onDownloadComplete}
        />
      </Stack>
    </GridToolbarContainer>
  );
};

export const FeedGrid: React.FC<IProps> = (props) => {
  const colDef = React.useMemo(() => makeGridColDef(), []);
  // statusがsucceededの行だけをフィルタリング
  const rows = React.useMemo(
    () => (props.row ?? []).filter((row) => row.status === "succeeded"),
    [props.row]
  );

  return (
    <StyledDataGrid
      components={{
        Toolbar: CustomToolbar,
      }}
      componentsProps={{
        toolbar: {
          requestId: props.requestId,
          disabledAction: props.disabledAction,
          onDownloadComplete: props.onDownloadComplete,
        },
      }}
      autoHeight
      disableColumnMenu
      disableSelectionOnClick={true}
      rows={rows}
      columns={colDef}
      sortModel={[
        {
          field: "id",
          sort: "asc",
        },
      ]}
    />
  );
};
